import {
  useInfiniteQuery,
  InfiniteData,
  useQueryClient,
} from "@tanstack/react-query";
import { ArticlesServiceImpl } from "../../domain/services/articlesService";
import { ArticlesResponse } from "../../data/models/article";

const ArticlesService = new ArticlesServiceImpl();

const useGetArticles = () => {
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery<ArticlesResponse>({
    queryKey: ["articles"],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      ArticlesService.getArticles(pageParam as number),
    getNextPageParam: (lastPage) => {
      const { page, pageCount } = lastPage.meta.pagination;
      return page < pageCount ? page + 1 : undefined;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    retry: 1,
  });

  const refetchWithPage = async (page?: number): Promise<ArticlesResponse> => {
    if (page) {
      const response = await ArticlesService.getArticles(page);
      // Update the cache with the new page data
      queryClient.setQueryData<InfiniteData<ArticlesResponse>>(
        ["articles"],
        (oldData) => {
          if (!oldData) {
            return {
              pages: [response],
              pageParams: [page],
            };
          }
          return {
            ...oldData,
            pages: [response],
            pageParams: [page],
          };
        },
      );
      return response;
    }
    const result = await refetch();
    if (!result.data) {
      throw new Error("Failed to fetch articles");
    }
    return result.data.pages[0];
  };

  return {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    refetch: refetchWithPage,
    isFetchingNextPage,
  };
};

export default useGetArticles;
